import { useAppDispatch, useAppSelector } from 'hooks/useAppDispatchSelector';
import { useContext, useState, useRef } from 'react';
import { AppContext } from 'context/app-context';
import { setModal } from 'store/features/modelSlice';
import { initModal } from 'helpers/utils';
import PrimaryDarkButton from 'components/common/buttons/primary-dark-btn';
import DarkButton from 'components/common/buttons/dark-btn';
import InputField from 'components/common/input-field';
import { ReactComponent as CrossIcon } from 'assets/icon/x-circle.svg';
import { createBrand, updateBrand } from 'store/features/vehicleConfigSlice';
import toast from 'react-hot-toast';
import PrimaryButtom from 'components/common/buttons/primary-btn';
import { IUploadImageRequestParam } from 'store/features/organizationSlice';
import { getVehicleUploadImageLocation } from 'store/features/vehicleSlice';
import { IBrand } from 'interfaces/vehicle-config-interfaces';
import { useTranslation } from 'react-i18next';

const AddEditBrand = () => {
  const dispatch = useAppDispatch();
  const {
    modal: {
      content: { title, formFor, callBackFun },
    },
  } = useAppSelector(state => state.modal);
  const { selectedBrand } = useAppSelector(state => state.vehicleConfig);
  const { setLoader } = useContext(AppContext);
  const [brand, setBrand] = useState<IBrand>(
    formFor === 'EDIT' && selectedBrand ? selectedBrand : { name: '' },
  );
  const [error, setError] = useState<string>('');
  const [brandImg, setBrandImg] = useState<{
    file?: null | File;
    url?: string;
    key?: string;
  } | null>(
    formFor === 'EDIT' && selectedBrand
      ? { url: selectedBrand?.image_url, key: selectedBrand?.assetDetails?.url }
      : null,
  );
  let logoRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();

  const uploadLogo = async () => {
    try {
      if (brandImg?.file) {
        setLoader(true);
        const params: IUploadImageRequestParam = {
          request: {
            file_name: brandImg?.file?.name || '',
            file_size: brandImg?.file?.size || 0,
            file_type: brandImg?.file?.type || '',
          },
          file: brandImg?.file,
        };
        const response: { key: string; download_url: string; url: string } = await dispatch(
          getVehicleUploadImageLocation({ params }),
        ).catch(() => {
          setLoader(false);
        });
        return Promise.resolve(response);
      }
    } catch (error) {
      setLoader(false);
    }
  };

  const handleAddBrand = async () => {
    try {
      setLoader(true);
      const imgData = await uploadLogo();
      await dispatch(createBrand({ name: brand?.name, image: imgData?.key || '' }))
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successBrandAddedToastMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleEditBrand = async () => {
    try {
      setLoader(true);
      let imgURL = brandImg?.key;
      if (brandImg?.file) {
        const response = await uploadLogo();
        imgURL = response?.key;
      }
      await dispatch(
        updateBrand({
          brand_id: brand?._id || '',
          request: {
            brand_id: brand?._id || '',
            name: brand.name,
            image: imgURL || '',
          },
        }),
      )
        .then(() => {
          callBackFun && callBackFun();
          toast.success(t('workshopAppointment.successBrandUpdatedToastMessage'));
          dispatch(setModal(initModal));
          setLoader(false);
        })
        .catch(() => {
          setLoader(false);
        });
      setLoader(false);
    } catch (error) {
      setLoader(false);
    }
  };
  const handleSubmit = () => {
    try {
      if (!brand?.name?.trim()) {
        setError(t('workshopAppointment.errorBrandNameToastMessage'));
        return;
      }
      formFor === 'EDIT' ? handleEditBrand() : handleAddBrand();
    } catch (error) {
      setLoader(false);
    }
  };
  const setBrandImgData = (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (event?.target.files?.length) {
        setLoader(true);
        setBrandImg({
          file: event?.target?.files[0],
          url: window.URL.createObjectURL(event?.target?.files[0]),
        });
        setLoader(false);
      }
    } catch (error) {
      setLoader(false);
    }
  };
  const handleUploadLogo = () => {
    const fileElement = logoRef.current;
    fileElement?.click();
  };
  return (
    <div className="flex flex-wrap max-w-[500px] w-[500px] gap-y-4 overflow-hidden">
      <div className="flex items-center w-full">
        <div className="flex items-center flex-1">
          <label className="pl-2 text-xl font-semibold text-black2">{title}</label>
        </div>
        <div
          className="flex items-center justify-center cursor-pointer"
          onClick={() => dispatch(setModal(initModal))}
        >
          <CrossIcon height={32} width={32} />
        </div>
      </div>
      <div className="flex flex-wrap w-full overflow-y-auto max-h-[calc(100vh- 200px)] gap-y-1">
        <div className="flex items-center self-center w-full px-3 pb-2 gap-x-2">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-grey2">
            {!brandImg?.url ? (
              <label className="text-sm font-medium text-white">{t('NO_IMG')}</label>
            ) : (
              <img
                src={brandImg?.url}
                alt="Org-LOGO"
                className="h-full w-full rounded-full object-contain"
              />
            )}
          </div>
          <div className="flex items-center justify-center">
            <input
              type="file"
              className="hidden"
              ref={logoRef}
              onChange={event => setBrandImgData(event)}
              accept="image/*"
            />
            <PrimaryButtom
              label={
                !brandImg?.url
                  ? t('workshopAppointment.uploadLogo')
                  : t('workshopAppointment.changeLogo')
              }
              onClick={() => handleUploadLogo()}
              height="h-8"
              font_size="text-sm"
            />
          </div>
        </div>
        <div className="w-full">
          <label className="font-medium text-xs text-black2 w-full">{t('BRAND')}</label>
          <InputField
            onChange={data => setBrand({ ...brand, name: data })}
            error={error}
            value={brand.name}
            placeholder={t('workshopAppointment.brandNamePlaceholder')}
          />
        </div>
      </div>
      <div className="flex items-center justify-center w-full gap-x-4 mt-4">
        <PrimaryDarkButton
          label={
            formFor === 'EDIT'
              ? t('workshopAppointment.saveChanges')
              : t('workshopAppointment.addBrand')
          }
          onClick={() => handleSubmit()}
          width="w-1/3"
        />
        <DarkButton
          label={t('CANCEL')}
          width="w-1/3"
          onClick={() => dispatch(setModal(initModal))}
        />
      </div>
    </div>
  );
};

export default AddEditBrand;
